import React, { createContext, useContext, useState, useCallback } from 'react';

const PostCacheContext = createContext();

const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

export const PostCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(new Map());

  const getCacheKey = (author, permlink) => `${author}/${permlink}`;

  const getFromCache = useCallback((author, permlink) => {
    const key = getCacheKey(author, permlink);
    const cached = cache.get(key);
    
    if (!cached) return null;
    
    const now = Date.now();
    if (now - cached.timestamp > CACHE_EXPIRATION_MS) {
      // Expired, remove from cache
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(key);
        return newCache;
      });
      return null;
    }
    
    return cached.data;
  }, [cache]);

  const addToCache = useCallback((author, permlink, data) => {
    const key = getCacheKey(author, permlink);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(key, {
        data,
        timestamp: Date.now()
      });
      return newCache;
    });
  }, []);

  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  const invalidatePost = useCallback((author, permlink) => {
    const key = getCacheKey(author, permlink);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(key);
      return newCache;
    });
  }, []);

  return (
    <PostCacheContext.Provider value={{ getFromCache, addToCache, clearCache, invalidatePost }}>
      {children}
    </PostCacheContext.Provider>
  );
};

export const usePostCache = () => {
  const context = useContext(PostCacheContext);
  if (!context) {
    throw new Error('usePostCache must be used within a PostCacheProvider');
  }
  return context;
};
